import { useCallback, useEffect, useMemo, useState } from 'react'
import { tasksApi } from '@carrent/api'
import { startOfDay } from './due'

/** Ключ дня в том же виде, в каком его отдаёт сервер. */
export function dayKey(date) {
  const d = new Date(date)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Сетка месяца: полные недели с понедельника.
 *
 * Неделя начинается с понедельника — это российский календарь,
 * и производственный календарь водителей устроен так же.
 */
export function monthGrid(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const start = startOfDay(first)
  // getDay(): воскресенье = 0. Сдвигаем к понедельнику.
  const shift = (start.getDay() + 6) % 7
  start.setDate(start.getDate() - shift)

  const weeks = []
  const cursor = new Date(start)
  // Шесть недель — постоянная высота сетки. Прыгающая на строку
  // высота при смене месяца сдвигает всё, что под календарём.
  for (let w = 0; w < 6; w += 1) {
    const week = []
    for (let d = 0; d < 7; d += 1) {
      week.push(new Date(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    weeks.push(week)
  }
  return weeks
}

export const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

export const MONTH_LABELS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

/**
 * Задачи месяца, разложенные по дням.
 *
 * Запрашивается видимый диапазон целиком, включая хвосты соседних
 * месяцев: они в сетке нарисованы и должны быть заполнены, иначе
 * первые числа выглядят пустыми на границе месяца.
 */
export function useTaskCalendar({ anchor, scope = 'all', openOnly = false } = {}) {
  const [days, setDays] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const weeks = useMemo(() => monthGrid(anchor), [anchor])

  const range = useMemo(() => {
    const first = weeks[0][0]
    const last = weeks[weeks.length - 1][6]
    const to = new Date(last)
    to.setHours(23, 59, 59, 999)
    return { from: first.toISOString(), to: to.toISOString() }
  }, [weeks])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = {
        from: range.from,
        to: range.to,
        // Знак обратный getTimezoneOffset: у Москвы getTimezoneOffset
        // равен −180, а сдвиг к местному времени — плюс 180 минут.
        tz_offset: -new Date().getTimezoneOffset(),
      }
      if (scope === 'mine') params.assigned_to = 'me'
      if (openOnly) params.open_only = 'true'

      const { data } = await tasksApi.calendar(params)
      setDays(data.data?.days || {})
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось загрузить календарь')
      setDays({})
    } finally {
      setLoading(false)
    }
  }, [range.from, range.to, scope, openOnly])

  useEffect(() => {
    load()
  }, [load])

  const tasksOn = useCallback((date) => days[dayKey(date)] || [], [days])

  const total = useMemo(
    () => Object.values(days).reduce((sum, list) => sum + list.length, 0),
    [days]
  )

  return { weeks, days, tasksOn, total, loading, error, reload: load }
}
